import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, Input, HostBinding, Component, ViewEncapsulation, ChangeDetectionStrategy, Optional, Inject, ViewChild, ContentChild, ContentChildren, NgModule } from '@angular/core';
import * as i4 from '@angular/cdk/observers';
import { ObserversModule } from '@angular/cdk/observers';
import { mixinColor } from '@paysafe-ui/components/core';
import { Subject, merge, fromEvent } from 'rxjs';
import { startWith, takeUntil, take } from 'rxjs/operators';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/cdk/bidi';
import * as i2 from '@angular/cdk/platform';
import { PsIconModule } from '@paysafe-ui/components/icon';

/** An interface which allows a control to work inside of a `PsFormFieldComponent`. */
class PsFormFieldControl {
}

/** @docs-private */
const getPsFormFieldPlaceholderConflictError = () => Error('Placeholder attribute and child element were both specified.');
/** @docs-private */
const getPsFormFieldDuplicatedHintError = (align) => Error(`A hint was already declared for 'align="${align}"'.`);
/** @docs-private */
const getPsFormFieldMissingControlError = () => Error('ps-form-field must contain a PsFormFieldControl.');

/**
 * Animations used by the PsFormFieldComponent.
 * @docs-private
 */
const psFormFieldAnimations = {
    /** Animation that transitions the form field's error and hint messages. */
    transitionMessages: trigger('transitionMessages', [
        // TODO(mmalerba): Use angular animations for label animation as well.
        state('enter', style({ opacity: 1, transform: 'translateY(0%)' })),
        transition('void => enter', [
            style({ opacity: 0, transform: 'translateY(-100%)' }),
            animate('300ms cubic-bezier(0.55, 0, 0.55, 0.2)')
        ])
    ])
};

let nextUniqueId$2 = 0;
/** Single error message to be shown underneath the form field. */
class PsErrorDirective {
    constructor() {
        this.id = `ps-error-${nextUniqueId$2++}`;
    }
    get psError() {
        return true;
    }
    get errorRole() {
        return 'alert';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsErrorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsErrorDirective, selector: "ps-error", inputs: { id: "id" }, host: { properties: { "attr.id": "this.id", "class.ps-error": "this.psError", "attr.role": "this.errorRole" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsErrorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-error'
                }]
        }], propDecorators: { id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.id']
            }], psError: [{
                type: HostBinding,
                args: ['class.ps-error']
            }], errorRole: [{
                type: HostBinding,
                args: ['attr.role']
            }] } });

let nextUniqueId$1 = 0;
/** Hint text to be shown underneath the form field control. */
class PsHintDirective {
    constructor() {
        /** Whether to align the hint label at the start or end of the line. */
        this.align = 'start';
        /** Unique ID for the hint. Used for the aria-describedby on the form field control. */
        this.id = `ps-hint-${nextUniqueId$1++}`;
    }
    psHint() {
        return true;
    }
    get isHintRight() {
        return this.align === 'end';
    }
    // Remove align attribute to prevent it from interfering with layout.
    get removeAlign() {
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHintDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsHintDirective, selector: "ps-hint", inputs: { align: "align", id: "id" }, host: { properties: { "attr.id": "this.id", "class.ps-hint": "this.psHint", "class.ps-right": "this.isHintRight", "attr.align": "this.removeAlign" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsHintDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-hint'
                }]
        }], propDecorators: { align: [{
                type: Input
            }], id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.id']
            }], psHint: [{
                type: HostBinding,
                args: ['class.ps-hint']
            }], isHintRight: [{
                type: HostBinding,
                args: ['class.ps-right']
            }], removeAlign: [{
                type: HostBinding,
                args: ['attr.align']
            }] } });

/** The floating label for a `ps-form-field`. */
class PsLabelDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsLabelDirective, selector: "ps-label", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-label'
                }]
        }] });

/**
 * The placeholder text for an `PsFormFieldComponent`.
 */
class PsPlaceholderDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsPlaceholderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsPlaceholderDirective, selector: "ps-placeholder", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsPlaceholderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ps-placeholder'
                }]
        }] });

/** Prefix to be placed in front of the form field. */
class PsPrefixDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsPrefixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsPrefixDirective, selector: "[psPrefix]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psPrefix]'
                }]
        }] });

/** Suffix to be placed at the end of the form field. */
class PsSuffixDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsSuffixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsSuffixDirective, selector: "[psSuffix]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[psSuffix]'
                }]
        }] });

let nextUniqueId = 0;
const floatingLabelScale = 0.75;
const outlineGapPadding = 5;
const nullValue = null;
/**
 * Boilerplate for applying mixins to PsFormField.
 * @docs-private
 */
class PsFormFieldBase {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
    }
}
/**
 * Base class to which we're applying the form field mixins.
 * @docs-private
 */
const _PsFormFieldMixinBase = mixinColor(PsFormFieldBase, 'secondary');
/** Container for form controls that applies Paysafe styling and behavior. */
class PsFormFieldComponent extends _PsFormFieldMixinBase {
    /** Whether the required marker should be hidden. */
    get hideRequiredMarker() {
        return this._hideRequiredMarker;
    }
    set hideRequiredMarker(value) {
        this._hideRequiredMarker = coerceBooleanProperty(value);
    }
    /** Whether the floating label should always float or not. */
    get _shouldAlwaysFloat() {
        return this.floatLabel === 'always' && !this._showAlwaysAnimate;
    }
    /** Whether the label can float or not. */
    get _canLabelFloat() {
        return this.floatLabel !== 'never';
    }
    /** Text for the form field hint. */
    get hintLabel() {
        return this._hintLabel;
    }
    set hintLabel(value) {
        this._hintLabel = value;
        this._processHints();
    }
    /**
     * Whether the label should always float, never float or float as the user types.
     *
     * Note: only the legacy appearance supports the `never` option. `never` was originally added as a
     * way to make the floating label emulate the behavior of a standard input placeholder. However
     * the form field now supports both floating labels and placeholders. Therefore in the non-legacy
     * appearances the `never` option has been disabled in favor of just using the placeholder.
     */
    get floatLabel() {
        return this.appearance !== 'legacy' && this._floatLabel === 'never' ? 'auto' : this._floatLabel;
    }
    set floatLabel(value) {
        if (value !== this._floatLabel) {
            this._floatLabel = value || this._labelOptions.float || 'auto';
            this._changeDetectorRef.markForCheck();
        }
    }
    get formFieldClasses() {
        return true;
    }
    get appreanceFill() {
        return true;
    }
    get isFormFieldInvalid() {
        return this._control.errorState;
    }
    get canFormFieldFloat() {
        return this._canLabelFloat;
    }
    get shouldFormFieldFloat() {
        return this._shouldLabelFloat();
    }
    get hasFormFieldFloat() {
        return this._hasFloatingLabel();
    }
    get hidePlaceholder() {
        return this._hideControlPlaceholder();
    }
    get isFormFieldDisabled() {
        return this._control.disabled;
    }
    get isFormFieldAutofilled() {
        return this._control.autofilled;
    }
    get isFormFieldFocused() {
        return this._control.focused;
    }
    get isSecondaryColor() {
        return this.color === 'secondary';
    }
    get isWarnColor() {
        return this.color === 'warn';
    }
    get isUntouched() {
        return this._shouldForward('untouched');
    }
    get isTouched() {
        return this._shouldForward('touched');
    }
    get isPristine() {
        return this._shouldForward('pristine');
    }
    get isDirty() {
        return this._shouldForward('dirty');
    }
    get isValid() {
        return this._shouldForward('valid');
    }
    get isInvalid() {
        return this._shouldForward('invalid');
    }
    get isPending() {
        return this._shouldForward('pending');
    }
    get isCompact() {
        return this._compact;
    }
    get _control() {
        return this._explicitFormFieldControl || this._controlNonStatic || this._controlStatic;
    }
    set _control(value) {
        this._explicitFormFieldControl = value;
    }
    get _labelChild() {
        return this._labelChildNonStatic || this._labelChildStatic;
    }
    constructor(_elementRef, _changeDetectorRef, _dir, _platform, _ngZone, _animationMode) {
        super(_elementRef);
        this._elementRef = _elementRef;
        this._changeDetectorRef = _changeDetectorRef;
        this._dir = _dir;
        this._platform = _platform;
        this._ngZone = _ngZone;
        /**
         * Whether the outline gap needs to be calculated
         * immediately on the next change detection run.
         */
        this._outlineGapCalculationNeededImmediately = false;
        /** Whether the outline gap needs to be calculated next time the zone has stabilized. */
        this._outlineGapCalculationNeededOnStable = false;
        this._destroyed = new Subject();
        /** The form-field appearance style. */
        this.appearance = 'fill';
        /** Override for the logic that disables the label animation in certain cases. */
        this._showAlwaysAnimate = false;
        /** State of the ps-hint and ps-error animations. */
        this._subscriptAnimationState = '';
        this._hintLabel = '';
        // Unique id for the hint label.
        this._hintLabelId = `ps-hint-${nextUniqueId++}`;
        // Unique id for the internal form field label.
        this._labelId = `ps-form-field-label-${nextUniqueId++}`;
        // Whether the form field is in compact mode.
        this._compact = false;
        this.hasTextareaElement = false;
        this.floatLabel = 'auto';
        this._animationsEnabled = _animationMode !== 'NoopAnimations';
    }
    /**
     * Gets the id of the label element. If no label is present, returns `null`.
     */
    getLabelId() {
        return this._hasFloatingLabel() ? this._labelId : null;
    }
    /**
     * Gets an ElementRef for the element that a overlay attached to the form-field should be
     * positioned relative to.
     */
    getConnectedOverlayOrigin() {
        return this._connectionContainerRef || this._elementRef;
    }
    ngAfterContentInit() {
        this._validateControlChild();
        const control = this._control;
        if (control.controlType) {
            this._elementRef.nativeElement.classList.add(`ps-form-field-type-${control.controlType}`);
        }
        // Subscribe to changes in the child control state in order to update the form field UI.
        control.stateChanges.pipe(startWith(nullValue)).subscribe(() => {
            this._validatePlaceholders();
            this._syncDescribedByIds();
            this._changeDetectorRef.markForCheck();
        });
        // Run change detection if the value changes.
        if (control.ngControl && control.ngControl.valueChanges) {
            control.ngControl.valueChanges
                .pipe(takeUntil(this._destroyed))
                .subscribe(() => this._changeDetectorRef.markForCheck());
        }
        // Note that we have to run outside of the `NgZone` explicitly,
        // in order to avoid throwing users into an infinite loop
        // if `zone-patch-rxjs` is included.
        this._ngZone.runOutsideAngular(() => {
            this._ngZone.onStable
                .asObservable()
                .pipe(takeUntil(this._destroyed))
                .subscribe(() => {
                if (this._outlineGapCalculationNeededOnStable) {
                    this.updateOutlineGap();
                }
            });
        });
        // Run change detection and update the outline if the suffix or prefix changes.
        merge(this._prefixChildren.changes, this._suffixChildren.changes).subscribe(() => {
            this._outlineGapCalculationNeededOnStable = true;
            this._changeDetectorRef.markForCheck();
        });
        // Re-validate when the number of hints changes.
        this._hintChildren.changes.pipe(startWith(nullValue)).subscribe(() => {
            this._processHints();
            this._changeDetectorRef.markForCheck();
        });
        // Update the aria-described by when the number of errors changes.
        this._errorChildren.changes.pipe(startWith(nullValue)).subscribe(() => {
            this._syncDescribedByIds();
            this._changeDetectorRef.markForCheck();
        });
        if (this._dir) {
            this._dir.change.pipe(takeUntil(this._destroyed)).subscribe(() => this.updateOutlineGap());
        }
    }
    ngAfterContentChecked() {
        this._validateControlChild();
        if (this._outlineGapCalculationNeededImmediately) {
            this.updateOutlineGap();
        }
    }
    ngAfterViewInit() {
        // Avoid animations on load.
        this._subscriptAnimationState = 'enter';
        this.hasTextareaElement = this._hasElement('textarea');
        if (this.hasTextareaElement) {
            this._elementRef.nativeElement.classList.add('ps-form-field-has-textarea');
        }
        this._changeDetectorRef.detectChanges();
    }
    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }
    /** Determines whether a class from the NgControl should be forwarded to the host element. */
    _shouldForward(prop) {
        const ngControl = this._control ? this._control.ngControl : null;
        return ngControl && ngControl[prop];
    }
    _hasPlaceholder() {
        return !!((this._control && this._control.placeholder) || this._placeholderChild);
    }
    _hasLabel() {
        return !!this._labelChild;
    }
    _shouldLabelFloat() {
        return this._canLabelFloat && (this._control.shouldLabelFloat || this._shouldAlwaysFloat);
    }
    _hideControlPlaceholder() {
        // In the legacy appearance the placeholder is promoted to a label if no label is given.
        return !this._hasLabel() || (this._hasLabel() && !this._shouldLabelFloat());
    }
    _hasFloatingLabel() {
        // In the legacy appearance the placeholder is promoted to a label if no label is given.
        return this._hasLabel() || this._hasPlaceholder();
    }
    _hasElement(selector) {
        const element = this._inputContainerRef.nativeElement;
        return !!element.querySelector(selector);
    }
    /** Determines whether to display hints or errors. */
    _getDisplayedMessages() {
        return this._errorChildren && this._errorChildren.length > 0 && this._control.errorState
            ? 'error'
            : 'hint';
    }
    /** Animates the placeholder up and locks it in position. */
    _animateAndLockLabel() {
        if (this._hasFloatingLabel() && this._canLabelFloat) {
            // If animations are disabled, we shouldn't go in here,
            // because the `transitionend` will never fire.
            if (this._animationsEnabled) {
                this._showAlwaysAnimate = true;
                fromEvent(this._label.nativeElement, 'transitionend')
                    .pipe(take(1))
                    .subscribe(() => {
                    this._showAlwaysAnimate = false;
                });
            }
            this.floatLabel = 'always';
            this._changeDetectorRef.markForCheck();
        }
    }
    /**
     * Ensure that there is only one placeholder (either `placeholder` attribute on the child control
     * or child element with the `ps-placeholder` directive).
     */
    _validatePlaceholders() {
        if (this._control.placeholder && this._placeholderChild) {
            throw getPsFormFieldPlaceholderConflictError();
        }
    }
    /** Does any extra processing that is required when handling the hints. */
    _processHints() {
        this._validateHints();
        this._syncDescribedByIds();
    }
    /**
     * Ensure that there is a maximum of one of each `<ps-hint>` alignment specified, with the
     * attribute being considered as `align="start"`.
     */
    _validateHints() {
        if (this._hintChildren) {
            let startHint;
            let endHint;
            this._hintChildren.forEach((hint) => {
                if (hint.align === 'start') {
                    if (startHint || this.hintLabel) {
                        throw getPsFormFieldDuplicatedHintError('start');
                    }
                    startHint = hint;
                }
                else if (hint.align === 'end') {
                    if (endHint) {
                        throw getPsFormFieldDuplicatedHintError('end');
                    }
                    endHint = hint;
                }
            });
        }
    }
    /**
     * Sets the list of element IDs that describe the child control. This allows the control to update
     * its `aria-describedby` attribute accordingly.
     */
    _syncDescribedByIds() {
        if (this._control) {
            let ids = [];
            if (this._getDisplayedMessages() === 'hint') {
                const startHint = this._hintChildren
                    ? this._hintChildren.find((hint) => hint.align === 'start')
                    : null;
                const endHint = this._hintChildren
                    ? this._hintChildren.find((hint) => hint.align === 'end')
                    : null;
                if (startHint) {
                    ids.push(startHint.id);
                }
                else if (this._hintLabel) {
                    ids.push(this._hintLabelId);
                }
                if (endHint) {
                    ids.push(endHint.id);
                }
            }
            else if (this._errorChildren) {
                ids = this._errorChildren.map((error) => error.id);
            }
            this._control.setDescribedByIds(ids);
        }
    }
    /** Throws an error if the form field's control is missing. */
    _validateControlChild() {
        if (!this._control) {
            throw getPsFormFieldMissingControlError();
        }
    }
    /**
     * Updates the width and position of the gap in the outline. Only relevant for the outline
     * appearance.
     */
    updateOutlineGap() {
        const labelEl = this._label ? this._label.nativeElement : null;
        if (this.appearance !== 'outline' ||
            !labelEl ||
            !labelEl.children.length ||
            !labelEl.textContent.trim()) {
            return;
        }
        if (!this._platform.isBrowser) {
            // getBoundingClientRect isn't available on the server.
            return;
        }
        // If the element is not present in the DOM, the outline gap will need to be calculated
        // the next time it is checked and in the DOM.
        if (!document.documentElement.contains(this._elementRef.nativeElement)) {
            this._outlineGapCalculationNeededImmediately = true;
            return;
        }
        let startWidth = 0;
        let gapWidth = 0;
        const container = this._connectionContainerRef.nativeElement;
        const startEls = container.querySelectorAll('.ps-form-field-outline-start');
        const gapEls = container.querySelectorAll('.ps-form-field-outline-gap');
        if (this._label && this._label.nativeElement.children.length) {
            const containerRect = container.getBoundingClientRect();
            // If the container's width and height are zero, it means that the element is
            // invisible and we can't calculate the outline gap. Mark the element as needing
            // to be checked the next time the zone stabilizes. We can't do this immediately
            // on the next change detection, because even if the element becomes visible,
            // the `ClientRect` won't be recalculated immediately. We reset the
            // `_outlineGapCalculationNeededImmediately` flag some we don't run the checks twice.
            if (containerRect.width === 0 && containerRect.height === 0) {
                this._outlineGapCalculationNeededOnStable = true;
                this._outlineGapCalculationNeededImmediately = false;
                return;
            }
            const containerStart = this._getStartEnd(containerRect);
            const labelStart = this._getStartEnd(labelEl.children[0].getBoundingClientRect());
            let labelWidth = 0;
            for (const child of labelEl.children) {
                labelWidth += child.offsetWidth;
            }
            startWidth = labelStart - containerStart - outlineGapPadding;
            gapWidth = labelWidth > 0 ? labelWidth * floatingLabelScale + outlineGapPadding * 2 : 0;
        }
        for (let i = 0; i < startEls.length; i++) {
            startEls.item(i).style.width = `${startWidth}px`;
        }
        for (let i = 0; i < gapEls.length; i++) {
            gapEls.item(i).style.width = `${gapWidth}px`;
        }
        this._outlineGapCalculationNeededImmediately = false;
        this._outlineGapCalculationNeededOnStable = this._outlineGapCalculationNeededImmediately;
    }
    /** Gets the start end of the rect considering the current directionality. */
    _getStartEnd(rect) {
        return this._dir && this._dir.value === 'rtl' ? rect.right : rect.left;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsFormFieldComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i1.Directionality, optional: true }, { token: i2.Platform }, { token: i0.NgZone }, { token: ANIMATION_MODULE_TYPE, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsFormFieldComponent, selector: "ps-form-field", inputs: { hideRequiredMarker: "hideRequiredMarker", hintLabel: "hintLabel", floatLabel: "floatLabel" }, host: { properties: { "class.ps-form-field": "this.formFieldClasses", "class.ps-form-field-appearance-fill": "this.appreanceFill", "class.ps-form-field-invalid": "this.isFormFieldInvalid", "class.ps-form-field-can-float": "this.canFormFieldFloat", "class.ps-form-field-should-float": "this.shouldFormFieldFloat", "class.ps-form-field-has-label": "this.hasFormFieldFloat", "class.ps-form-field-hide-placeholder": "this.hidePlaceholder", "class.ps-form-field-disabled": "this.isFormFieldDisabled", "class.ps-form-field-autofilled": "this.isFormFieldAutofilled", "class.ps-focused": "this.isFormFieldFocused", "class.ps-secondary": "this.isSecondaryColor", "class.ps-warn": "this.isWarnColor", "class.ng-untouched": "this.isUntouched", "class.ng-touched": "this.isTouched", "class.ng-pristine": "this.isPristine", "class.ng-dirty": "this.isDirty", "class.ng-valid": "this.isValid", "class.ng-invalid": "this.isInvalid", "class.ng-pending": "this.isPending", "class.ps-form-field-compact": "this.isCompact" } }, queries: [{ propertyName: "_controlNonStatic", first: true, predicate: PsFormFieldControl, descendants: true }, { propertyName: "_controlStatic", first: true, predicate: PsFormFieldControl, descendants: true, static: true }, { propertyName: "_labelChildNonStatic", first: true, predicate: PsLabelDirective, descendants: true }, { propertyName: "_labelChildStatic", first: true, predicate: PsLabelDirective, descendants: true, static: true }, { propertyName: "_placeholderChild", first: true, predicate: PsPlaceholderDirective, descendants: true }, { propertyName: "_errorChildren", predicate: PsErrorDirective }, { propertyName: "_hintChildren", predicate: PsHintDirective }, { propertyName: "_prefixChildren", predicate: PsPrefixDirective }, { propertyName: "_suffixChildren", predicate: PsSuffixDirective }], viewQueries: [{ propertyName: "_connectionContainerRef", first: true, predicate: ["connectionContainer"], descendants: true, static: true }, { propertyName: "_inputContainerRef", first: true, predicate: ["inputContainer"], descendants: true }, { propertyName: "_label", first: true, predicate: ["label"], descendants: true }], exportAs: ["psFormField"], usesInheritance: true, ngImport: i0, template: "<div class=\"ps-form-field-wrapper\" [class.ps-form-field-wrapper-empty]=\"_control.empty\">\n  <div\n    class=\"ps-form-field-flex\"\n    [class.ps-form-field-textarea]=\"hasTextareaElement\"\n    #connectionContainer\n    (click)=\"_control.onContainerClick($event)\"\n  >\n    <div class=\"ps-form-field-prefix\" *ngIf=\"_prefixChildren.length\">\n      <ng-content select=\"[psPrefix]\"></ng-content>\n    </div>\n\n    <div class=\"ps-form-field-infix\" #inputContainer>\n      <ng-content></ng-content>\n\n      <span class=\"ps-form-field-label-wrapper\">\n        <!-- We add aria-owns as a workaround for an issue in JAWS & NVDA where the label isn't\n             read if it comes before the control in the DOM -->\n        <label\n          class=\"ps-form-field-label\"\n          (cdkObserveContent)=\"updateOutlineGap()\"\n          [id]=\"_labelId\"\n          [attr.for]=\"_control.id\"\n          [attr.aria-owns]=\"_control.id\"\n          [class.ps-empty]=\"_control.empty && !_shouldAlwaysFloat\"\n          [class.ps-form-field-empty]=\"_control.empty && !_shouldAlwaysFloat\"\n          [class.ps-secondary]=\"color == 'secondary'\"\n          [class.ps-warn]=\"color == 'warn'\"\n          #label\n          *ngIf=\"_hasFloatingLabel()\"\n          [ngSwitch]=\"_hasLabel()\"\n        >\n          <!-- @breaking-change 8.0.0 remove in favor of ps-label element an placeholder attr -->\n          <ng-container *ngSwitchCase=\"false\">\n            <ng-content select=\"ps-placeholder\"></ng-content>\n            <span>{{_control.placeholder}}</span>\n          </ng-container>\n\n          <ng-content select=\"ps-label\" *ngSwitchCase=\"true\"></ng-content>\n\n          <!-- @breaking-change 8.0.0 remove `ps-placeholder-required` class -->\n          <span\n            class=\"ps-placeholder-required ps-form-field-required-marker\"\n            aria-hidden=\"true\"\n            *ngIf=\"!hideRequiredMarker && _control.required && !_control.disabled\"\n            >&#32;*</span\n          >\n        </label>\n      </span>\n    </div>\n\n    <div class=\"ps-form-field-suffix\" *ngIf=\"_suffixChildren.length\">\n      <ng-content select=\"[psSuffix]\"></ng-content>\n    </div>\n  </div>\n\n  <!-- Underline used for legacy, standard, and box appearances. -->\n  <div class=\"ps-form-field-underline\">\n    <span\n      class=\"ps-form-field-ripple\"\n      [class.ps-secondary]=\"color == 'secondary'\"\n      [class.ps-warn]=\"color == 'warn'\"\n    ></span>\n  </div>\n\n  <div class=\"ps-form-field-subscript-wrapper\">\n    <div class=\"switch\" [ngSwitch]=\"_getDisplayedMessages()\">\n      <div\n        class=\"ps-form-field-hint\"\n        *ngSwitchCase=\"'hint'\"\n        [@transitionMessages]=\"_subscriptAnimationState\"\n      >\n        <ng-content select=\"ps-hint\"></ng-content>\n      </div>\n\n      <div *ngSwitchCase=\"'error'\" [@transitionMessages]=\"_subscriptAnimationState\">\n        <ng-content select=\"ps-error\"></ng-content>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".ps-form-field{display:inline-block;position:relative;border-bottom-left-radius:0;border-bottom-right-radius:0;text-align:left;height:4.75rem}.ps-form-field.ps-form-field-has-textarea{height:unset}[dir=rtl] .ps-form-field{text-align:right}.ps-form-field-wrapper{position:relative}.ps-form-field-flex{display:inline-flex;box-sizing:border-box;align-items:center;width:100%;min-height:3.5rem}.ps-form-field-prefix,.ps-form-field-suffix{white-space:nowrap;flex:none;position:relative}.ps-form-field-textarea{padding:.75rem .75rem .5rem!important}.ps-form-field-infix{display:block;position:relative;flex:auto;min-width:0;width:11.25rem}.ps-form-field-infix textarea{min-height:6.15625rem;max-height:15.25rem;line-height:1.5rem}@media (-ms-high-contrast: active){.ps-form-field-infix{border-image:linear-gradient(transparent,transparent)}}.ps-form-field-label-wrapper{position:absolute;left:0;box-sizing:content-box;width:100%;height:100%;overflow:hidden;pointer-events:none}[dir=rtl] .ps-form-field-label-wrapper{left:auto;right:0}.ps-form-field-label{position:absolute;left:0;font:inherit;pointer-events:none;width:100%;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;transform-origin:0 0;transition:transform .4s cubic-bezier(.25,.8,.25,1),color .4s cubic-bezier(.25,.8,.25,1),width .4s cubic-bezier(.25,.8,.25,1);display:none}[dir=rtl] .ps-form-field-label{transform-origin:100% 0;left:auto;right:0}.ps-form-field-empty.ps-form-field-label,.ps-form-field-can-float.ps-form-field-should-float .ps-form-field-label{display:block}.ps-form-field-autofill-control:-webkit-autofill+.ps-form-field-label-wrapper .ps-form-field-label{display:none}.ps-form-field-can-float .ps-form-field-autofill-control:-webkit-autofill+.ps-form-field-label-wrapper .ps-form-field-label{display:block;-webkit-transition:none;transition:none}.ps-input-server:focus+.ps-form-field-label-wrapper .ps-form-field-label,.ps-input-server[placeholder]:not(:placeholder-shown)+.ps-form-field-label-wrapper .ps-form-field-label{display:none}.ps-form-field-can-float .ps-input-server:focus+.ps-form-field-label-wrapper .ps-form-field-label,.ps-form-field-can-float .ps-input-server[placeholder]:not(:placeholder-shown)+.ps-form-field-label-wrapper .ps-form-field-label{display:block}.ps-form-field-label:not(.ps-form-field-empty){transition:none}.ps-form-field-underline{position:absolute;width:100%;pointer-events:none;transform:scaleY(1.0001)}.ps-form-field-ripple{position:absolute;left:0;width:100%;transform-origin:50%;transform:scaleX(.5);opacity:0;transition:background-color .3s cubic-bezier(.55,0,.55,.2)}.ps-form-field.ps-focused .ps-form-field-ripple,.ps-form-field.ps-form-field-invalid .ps-form-field-ripple{opacity:1;transform:scaleX(1);transition:transform .3s cubic-bezier(.25,.8,.25,1),opacity .1s cubic-bezier(.25,.8,.25,1),background-color .3s cubic-bezier(.25,.8,.25,1)}.ps-form-field-subscript-wrapper{position:absolute;box-sizing:border-box;width:100%;overflow:hidden}.ps-form-field-subscript-wrapper .ps-icon,.ps-form-field-label-wrapper .ps-icon{width:1em;height:1em;font-size:inherit;vertical-align:baseline}.ps-form-field-hint-wrapper{display:flex}.ps-form-field-hint-spacer{flex:1 0 1em}.ps-error{display:block;min-height:1rem}.ps-error .ps-icon{margin-right:.25rem;font-size:.9375rem;vertical-align:text-bottom;transform:scale(1.005) perspective(1px)}.ps-form-field-control-wrapper{position:relative}.ps-form-field.ps-anipsion-noopable .ps-form-field-label,.ps-form-field.ps-anipsion-noopable .ps-form-field-ripple{transition:none}\n", ".ps-form-field-appearance-fill .ps-form-field-flex{padding:0 .75em}@media (-ms-high-contrast: active){.ps-form-field-appearance-fill .ps-form-field-flex{outline:solid 1px}}.ps-form-field-appearance-fill.ps-form-field-compact{height:auto}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-wrapper{padding-bottom:0}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex{align-items:center;justify-content:center;border-radius:.5rem;padding:.75rem;min-height:2.5rem}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex .ps-form-field-prefix{margin-right:.5rem}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex .ps-form-field-infix{border-top:none;width:7.5rem}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex .ps-select-arrow-wrapper{transform:none;vertical-align:middle}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-underline{display:none}.ps-form-field-appearance-fill .ps-form-field-underline:before{content:\"\";display:block;position:absolute;bottom:0;height:1px;width:100%}.ps-form-field-appearance-fill .ps-form-field-ripple{bottom:0;height:2px}@media (-ms-high-contrast: active){.ps-form-field-appearance-fill .ps-form-field-ripple{height:0;border-top:solid 2px}}.ps-form-field-appearance-fill .ps-form-field-subscript-wrapper{padding:0 1em}.ps-form-field-appearance-fill .ps-form-field-hint .ps-hint{display:block}\n", ".ps-input-element{font:inherit;background:transparent;color:currentcolor;border:none;outline:none;padding:0;margin:0;width:100%;height:1.5rem;min-height:1.5rem;max-width:100%;vertical-align:bottom;text-align:inherit}.ps-input-element:-moz-ui-invalid{box-shadow:none}.ps-input-element::-ms-clear,.ps-input-element::-ms-reveal{display:none}.ps-input-element,.ps-input-element::-webkit-search-cancel-button,.ps-input-element::-webkit-search-decoration,.ps-input-element::-webkit-search-results-button,.ps-input-element::-webkit-search-results-decoration{appearance:none}.ps-input-element::-webkit-contacts-auto-fill-button,.ps-input-element::-webkit-caps-lock-indicator,.ps-input-element::-webkit-credentials-auto-fill-button{visibility:hidden}.ps-input-element[type=date]:after,.ps-input-element[type=datetime]:after,.ps-input-element[type=datetime-local]:after,.ps-input-element[type=month]:after,.ps-input-element[type=week]:after,.ps-input-element[type=time]:after{content:\" \";white-space:pre;width:.0625rem}.ps-input-element::-webkit-inner-spin-button,.ps-input-element::-webkit-calendar-picker-indicator,.ps-input-element::-webkit-clear-button{font-size:.75em}.ps-input-element::placeholder{-webkit-user-select:none;user-select:none;transition:color .4s .1333333333333s cubic-bezier(.25,.8,.25,1)}.ps-input-element::placeholder::-input-placeholder{-webkit-user-select:text;user-select:text}.ps-input-element::-input-placeholder{-webkit-user-select:none;user-select:none;transition:color .4s .1333333333333s cubic-bezier(.25,.8,.25,1)}.ps-input-element::-input-placeholder::-input-placeholder{-webkit-user-select:text;user-select:text}.ps-form-field-hide-placeholder .ps-input-element::placeholder{color:transparent!important;-webkit-text-fill-color:transparent;transition:none}.ps-form-field-hide-placeholder .ps-input-element::-input-placeholder{color:transparent!important;-webkit-text-fill-color:transparent;transition:none}textarea.ps-input-element{resize:vertical;overflow:auto}textarea.ps-input-element.cdk-textarea-autosize{resize:none}select.ps-input-element{appearance:none;position:relative;background-color:transparent;display:inline-flex;box-sizing:border-box;padding-top:1em;top:-1em;margin-bottom:-1em}select.ps-input-element::-ms-expand{display:none}select.ps-input-element::-moz-focus-inner{border:0}select.ps-input-element:not(:disabled){cursor:pointer}select.ps-input-element::-ms-value{color:inherit;background:none}@media (-ms-high-contrast: active){.ps-focused select.ps-input-element::-ms-value{color:inherit}}.ps-form-field-type-ps-native-select .ps-form-field-infix:after{content:\"\";width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid;position:absolute;top:50%;right:0;margin-top:-2.5px;pointer-events:none}[dir=rtl] .ps-form-field-type-ps-native-select .ps-form-field-infix:after{right:auto;left:0}.ps-form-field-type-ps-native-select .ps-input-element{padding-right:15px}[dir=rtl] .ps-form-field-type-ps-native-select .ps-input-element{padding-right:0;padding-left:15px}.ps-form-field-type-ps-native-select .ps-form-field-label-wrapper{max-width:calc(100% - 10px)}.ps-form-field-type-ps-native-select.ps-form-field-appearance-outline .ps-form-field-infix:after{margin-top:-5px}.ps-form-field-type-ps-native-select.ps-form-field-appearance-fill .ps-form-field-infix:after{margin-top:-10px}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i3.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i4.CdkObserveContent, selector: "[cdkObserveContent]", inputs: ["cdkObserveContentDisabled", "debounce"], outputs: ["cdkObserveContent"], exportAs: ["cdkObserveContent"] }], animations: [psFormFieldAnimations.transitionMessages], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsFormFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-form-field', exportAs: 'psFormField', animations: [psFormFieldAnimations.transitionMessages], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"ps-form-field-wrapper\" [class.ps-form-field-wrapper-empty]=\"_control.empty\">\n  <div\n    class=\"ps-form-field-flex\"\n    [class.ps-form-field-textarea]=\"hasTextareaElement\"\n    #connectionContainer\n    (click)=\"_control.onContainerClick($event)\"\n  >\n    <div class=\"ps-form-field-prefix\" *ngIf=\"_prefixChildren.length\">\n      <ng-content select=\"[psPrefix]\"></ng-content>\n    </div>\n\n    <div class=\"ps-form-field-infix\" #inputContainer>\n      <ng-content></ng-content>\n\n      <span class=\"ps-form-field-label-wrapper\">\n        <!-- We add aria-owns as a workaround for an issue in JAWS & NVDA where the label isn't\n             read if it comes before the control in the DOM -->\n        <label\n          class=\"ps-form-field-label\"\n          (cdkObserveContent)=\"updateOutlineGap()\"\n          [id]=\"_labelId\"\n          [attr.for]=\"_control.id\"\n          [attr.aria-owns]=\"_control.id\"\n          [class.ps-empty]=\"_control.empty && !_shouldAlwaysFloat\"\n          [class.ps-form-field-empty]=\"_control.empty && !_shouldAlwaysFloat\"\n          [class.ps-secondary]=\"color == 'secondary'\"\n          [class.ps-warn]=\"color == 'warn'\"\n          #label\n          *ngIf=\"_hasFloatingLabel()\"\n          [ngSwitch]=\"_hasLabel()\"\n        >\n          <!-- @breaking-change 8.0.0 remove in favor of ps-label element an placeholder attr -->\n          <ng-container *ngSwitchCase=\"false\">\n            <ng-content select=\"ps-placeholder\"></ng-content>\n            <span>{{_control.placeholder}}</span>\n          </ng-container>\n\n          <ng-content select=\"ps-label\" *ngSwitchCase=\"true\"></ng-content>\n\n          <!-- @breaking-change 8.0.0 remove `ps-placeholder-required` class -->\n          <span\n            class=\"ps-placeholder-required ps-form-field-required-marker\"\n            aria-hidden=\"true\"\n            *ngIf=\"!hideRequiredMarker && _control.required && !_control.disabled\"\n            >&#32;*</span\n          >\n        </label>\n      </span>\n    </div>\n\n    <div class=\"ps-form-field-suffix\" *ngIf=\"_suffixChildren.length\">\n      <ng-content select=\"[psSuffix]\"></ng-content>\n    </div>\n  </div>\n\n  <!-- Underline used for legacy, standard, and box appearances. -->\n  <div class=\"ps-form-field-underline\">\n    <span\n      class=\"ps-form-field-ripple\"\n      [class.ps-secondary]=\"color == 'secondary'\"\n      [class.ps-warn]=\"color == 'warn'\"\n    ></span>\n  </div>\n\n  <div class=\"ps-form-field-subscript-wrapper\">\n    <div class=\"switch\" [ngSwitch]=\"_getDisplayedMessages()\">\n      <div\n        class=\"ps-form-field-hint\"\n        *ngSwitchCase=\"'hint'\"\n        [@transitionMessages]=\"_subscriptAnimationState\"\n      >\n        <ng-content select=\"ps-hint\"></ng-content>\n      </div>\n\n      <div *ngSwitchCase=\"'error'\" [@transitionMessages]=\"_subscriptAnimationState\">\n        <ng-content select=\"ps-error\"></ng-content>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".ps-form-field{display:inline-block;position:relative;border-bottom-left-radius:0;border-bottom-right-radius:0;text-align:left;height:4.75rem}.ps-form-field.ps-form-field-has-textarea{height:unset}[dir=rtl] .ps-form-field{text-align:right}.ps-form-field-wrapper{position:relative}.ps-form-field-flex{display:inline-flex;box-sizing:border-box;align-items:center;width:100%;min-height:3.5rem}.ps-form-field-prefix,.ps-form-field-suffix{white-space:nowrap;flex:none;position:relative}.ps-form-field-textarea{padding:.75rem .75rem .5rem!important}.ps-form-field-infix{display:block;position:relative;flex:auto;min-width:0;width:11.25rem}.ps-form-field-infix textarea{min-height:6.15625rem;max-height:15.25rem;line-height:1.5rem}@media (-ms-high-contrast: active){.ps-form-field-infix{border-image:linear-gradient(transparent,transparent)}}.ps-form-field-label-wrapper{position:absolute;left:0;box-sizing:content-box;width:100%;height:100%;overflow:hidden;pointer-events:none}[dir=rtl] .ps-form-field-label-wrapper{left:auto;right:0}.ps-form-field-label{position:absolute;left:0;font:inherit;pointer-events:none;width:100%;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;transform-origin:0 0;transition:transform .4s cubic-bezier(.25,.8,.25,1),color .4s cubic-bezier(.25,.8,.25,1),width .4s cubic-bezier(.25,.8,.25,1);display:none}[dir=rtl] .ps-form-field-label{transform-origin:100% 0;left:auto;right:0}.ps-form-field-empty.ps-form-field-label,.ps-form-field-can-float.ps-form-field-should-float .ps-form-field-label{display:block}.ps-form-field-autofill-control:-webkit-autofill+.ps-form-field-label-wrapper .ps-form-field-label{display:none}.ps-form-field-can-float .ps-form-field-autofill-control:-webkit-autofill+.ps-form-field-label-wrapper .ps-form-field-label{display:block;-webkit-transition:none;transition:none}.ps-input-server:focus+.ps-form-field-label-wrapper .ps-form-field-label,.ps-input-server[placeholder]:not(:placeholder-shown)+.ps-form-field-label-wrapper .ps-form-field-label{display:none}.ps-form-field-can-float .ps-input-server:focus+.ps-form-field-label-wrapper .ps-form-field-label,.ps-form-field-can-float .ps-input-server[placeholder]:not(:placeholder-shown)+.ps-form-field-label-wrapper .ps-form-field-label{display:block}.ps-form-field-label:not(.ps-form-field-empty){transition:none}.ps-form-field-underline{position:absolute;width:100%;pointer-events:none;transform:scaleY(1.0001)}.ps-form-field-ripple{position:absolute;left:0;width:100%;transform-origin:50%;transform:scaleX(.5);opacity:0;transition:background-color .3s cubic-bezier(.55,0,.55,.2)}.ps-form-field.ps-focused .ps-form-field-ripple,.ps-form-field.ps-form-field-invalid .ps-form-field-ripple{opacity:1;transform:scaleX(1);transition:transform .3s cubic-bezier(.25,.8,.25,1),opacity .1s cubic-bezier(.25,.8,.25,1),background-color .3s cubic-bezier(.25,.8,.25,1)}.ps-form-field-subscript-wrapper{position:absolute;box-sizing:border-box;width:100%;overflow:hidden}.ps-form-field-subscript-wrapper .ps-icon,.ps-form-field-label-wrapper .ps-icon{width:1em;height:1em;font-size:inherit;vertical-align:baseline}.ps-form-field-hint-wrapper{display:flex}.ps-form-field-hint-spacer{flex:1 0 1em}.ps-error{display:block;min-height:1rem}.ps-error .ps-icon{margin-right:.25rem;font-size:.9375rem;vertical-align:text-bottom;transform:scale(1.005) perspective(1px)}.ps-form-field-control-wrapper{position:relative}.ps-form-field.ps-anipsion-noopable .ps-form-field-label,.ps-form-field.ps-anipsion-noopable .ps-form-field-ripple{transition:none}\n", ".ps-form-field-appearance-fill .ps-form-field-flex{padding:0 .75em}@media (-ms-high-contrast: active){.ps-form-field-appearance-fill .ps-form-field-flex{outline:solid 1px}}.ps-form-field-appearance-fill.ps-form-field-compact{height:auto}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-wrapper{padding-bottom:0}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex{align-items:center;justify-content:center;border-radius:.5rem;padding:.75rem;min-height:2.5rem}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex .ps-form-field-prefix{margin-right:.5rem}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex .ps-form-field-infix{border-top:none;width:7.5rem}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-flex .ps-select-arrow-wrapper{transform:none;vertical-align:middle}.ps-form-field-appearance-fill.ps-form-field-compact .ps-form-field-underline{display:none}.ps-form-field-appearance-fill .ps-form-field-underline:before{content:\"\";display:block;position:absolute;bottom:0;height:1px;width:100%}.ps-form-field-appearance-fill .ps-form-field-ripple{bottom:0;height:2px}@media (-ms-high-contrast: active){.ps-form-field-appearance-fill .ps-form-field-ripple{height:0;border-top:solid 2px}}.ps-form-field-appearance-fill .ps-form-field-subscript-wrapper{padding:0 1em}.ps-form-field-appearance-fill .ps-form-field-hint .ps-hint{display:block}\n", ".ps-input-element{font:inherit;background:transparent;color:currentcolor;border:none;outline:none;padding:0;margin:0;width:100%;height:1.5rem;min-height:1.5rem;max-width:100%;vertical-align:bottom;text-align:inherit}.ps-input-element:-moz-ui-invalid{box-shadow:none}.ps-input-element::-ms-clear,.ps-input-element::-ms-reveal{display:none}.ps-input-element,.ps-input-element::-webkit-search-cancel-button,.ps-input-element::-webkit-search-decoration,.ps-input-element::-webkit-search-results-button,.ps-input-element::-webkit-search-results-decoration{appearance:none}.ps-input-element::-webkit-contacts-auto-fill-button,.ps-input-element::-webkit-caps-lock-indicator,.ps-input-element::-webkit-credentials-auto-fill-button{visibility:hidden}.ps-input-element[type=date]:after,.ps-input-element[type=datetime]:after,.ps-input-element[type=datetime-local]:after,.ps-input-element[type=month]:after,.ps-input-element[type=week]:after,.ps-input-element[type=time]:after{content:\" \";white-space:pre;width:.0625rem}.ps-input-element::-webkit-inner-spin-button,.ps-input-element::-webkit-calendar-picker-indicator,.ps-input-element::-webkit-clear-button{font-size:.75em}.ps-input-element::placeholder{-webkit-user-select:none;user-select:none;transition:color .4s .1333333333333s cubic-bezier(.25,.8,.25,1)}.ps-input-element::placeholder::-input-placeholder{-webkit-user-select:text;user-select:text}.ps-input-element::-input-placeholder{-webkit-user-select:none;user-select:none;transition:color .4s .1333333333333s cubic-bezier(.25,.8,.25,1)}.ps-input-element::-input-placeholder::-input-placeholder{-webkit-user-select:text;user-select:text}.ps-form-field-hide-placeholder .ps-input-element::placeholder{color:transparent!important;-webkit-text-fill-color:transparent;transition:none}.ps-form-field-hide-placeholder .ps-input-element::-input-placeholder{color:transparent!important;-webkit-text-fill-color:transparent;transition:none}textarea.ps-input-element{resize:vertical;overflow:auto}textarea.ps-input-element.cdk-textarea-autosize{resize:none}select.ps-input-element{appearance:none;position:relative;background-color:transparent;display:inline-flex;box-sizing:border-box;padding-top:1em;top:-1em;margin-bottom:-1em}select.ps-input-element::-ms-expand{display:none}select.ps-input-element::-moz-focus-inner{border:0}select.ps-input-element:not(:disabled){cursor:pointer}select.ps-input-element::-ms-value{color:inherit;background:none}@media (-ms-high-contrast: active){.ps-focused select.ps-input-element::-ms-value{color:inherit}}.ps-form-field-type-ps-native-select .ps-form-field-infix:after{content:\"\";width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid;position:absolute;top:50%;right:0;margin-top:-2.5px;pointer-events:none}[dir=rtl] .ps-form-field-type-ps-native-select .ps-form-field-infix:after{right:auto;left:0}.ps-form-field-type-ps-native-select .ps-input-element{padding-right:15px}[dir=rtl] .ps-form-field-type-ps-native-select .ps-input-element{padding-right:0;padding-left:15px}.ps-form-field-type-ps-native-select .ps-form-field-label-wrapper{max-width:calc(100% - 10px)}.ps-form-field-type-ps-native-select.ps-form-field-appearance-outline .ps-form-field-infix:after{margin-top:-5px}.ps-form-field-type-ps-native-select.ps-form-field-appearance-fill .ps-form-field-infix:after{margin-top:-10px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1.Directionality, decorators: [{
                    type: Optional
                }] }, { type: i2.Platform }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ANIMATION_MODULE_TYPE]
                }] }], propDecorators: { hideRequiredMarker: [{
                type: Input
            }], hintLabel: [{
                type: Input
            }], floatLabel: [{
                type: Input
            }], formFieldClasses: [{
                type: HostBinding,
                args: ['class.ps-form-field']
            }], appreanceFill: [{
                type: HostBinding,
                args: ['class.ps-form-field-appearance-fill']
            }], isFormFieldInvalid: [{
                type: HostBinding,
                args: ['class.ps-form-field-invalid']
            }], canFormFieldFloat: [{
                type: HostBinding,
                args: ['class.ps-form-field-can-float']
            }], shouldFormFieldFloat: [{
                type: HostBinding,
                args: ['class.ps-form-field-should-float']
            }], hasFormFieldFloat: [{
                type: HostBinding,
                args: ['class.ps-form-field-has-label']
            }], hidePlaceholder: [{
                type: HostBinding,
                args: ['class.ps-form-field-hide-placeholder']
            }], isFormFieldDisabled: [{
                type: HostBinding,
                args: ['class.ps-form-field-disabled']
            }], isFormFieldAutofilled: [{
                type: HostBinding,
                args: ['class.ps-form-field-autofilled']
            }], isFormFieldFocused: [{
                type: HostBinding,
                args: ['class.ps-focused']
            }], isSecondaryColor: [{
                type: HostBinding,
                args: ['class.ps-secondary']
            }], isWarnColor: [{
                type: HostBinding,
                args: ['class.ps-warn']
            }], isUntouched: [{
                type: HostBinding,
                args: ['class.ng-untouched']
            }], isTouched: [{
                type: HostBinding,
                args: ['class.ng-touched']
            }], isPristine: [{
                type: HostBinding,
                args: ['class.ng-pristine']
            }], isDirty: [{
                type: HostBinding,
                args: ['class.ng-dirty']
            }], isValid: [{
                type: HostBinding,
                args: ['class.ng-valid']
            }], isInvalid: [{
                type: HostBinding,
                args: ['class.ng-invalid']
            }], isPending: [{
                type: HostBinding,
                args: ['class.ng-pending']
            }], isCompact: [{
                type: HostBinding,
                args: ['class.ps-form-field-compact']
            }], _connectionContainerRef: [{
                type: ViewChild,
                args: ['connectionContainer', { static: true }]
            }], _inputContainerRef: [{
                type: ViewChild,
                args: ['inputContainer', { static: false }]
            }], _label: [{
                type: ViewChild,
                args: ['label', { static: false }]
            }], _controlNonStatic: [{
                type: ContentChild,
                args: [PsFormFieldControl, { static: false }]
            }], _controlStatic: [{
                type: ContentChild,
                args: [PsFormFieldControl, { static: true }]
            }], _labelChildNonStatic: [{
                type: ContentChild,
                args: [PsLabelDirective, { static: false }]
            }], _labelChildStatic: [{
                type: ContentChild,
                args: [PsLabelDirective, { static: true }]
            }], _placeholderChild: [{
                type: ContentChild,
                args: [PsPlaceholderDirective, { static: false }]
            }], _errorChildren: [{
                type: ContentChildren,
                args: [PsErrorDirective]
            }], _hintChildren: [{
                type: ContentChildren,
                args: [PsHintDirective]
            }], _prefixChildren: [{
                type: ContentChildren,
                args: [PsPrefixDirective]
            }], _suffixChildren: [{
                type: ContentChildren,
                args: [PsSuffixDirective]
            }] } });

class PsFormFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsFormFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PsFormFieldModule, declarations: [PsErrorDirective,
            PsFormFieldComponent,
            PsHintDirective,
            PsLabelDirective,
            PsPlaceholderDirective,
            PsPrefixDirective,
            PsSuffixDirective], imports: [CommonModule, ObserversModule, PsIconModule], exports: [PsErrorDirective,
            PsFormFieldComponent,
            PsHintDirective,
            PsLabelDirective,
            PsPlaceholderDirective,
            PsPrefixDirective,
            PsSuffixDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsFormFieldModule, imports: [CommonModule, ObserversModule, PsIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsFormFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PsErrorDirective,
                        PsFormFieldComponent,
                        PsHintDirective,
                        PsLabelDirective,
                        PsPlaceholderDirective,
                        PsPrefixDirective,
                        PsSuffixDirective
                    ],
                    imports: [CommonModule, ObserversModule, PsIconModule],
                    exports: [
                        PsErrorDirective,
                        PsFormFieldComponent,
                        PsHintDirective,
                        PsLabelDirective,
                        PsPlaceholderDirective,
                        PsPrefixDirective,
                        PsSuffixDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PsErrorDirective, PsFormFieldComponent, PsFormFieldControl, PsFormFieldModule, PsHintDirective, PsLabelDirective, PsPlaceholderDirective, PsPrefixDirective, PsSuffixDirective, getPsFormFieldDuplicatedHintError, getPsFormFieldMissingControlError, getPsFormFieldPlaceholderConflictError, psFormFieldAnimations };
